import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, ClipboardList, Clock, DollarSign, MapPin, LogOut, Calendar, Users, UserCog, BarChart3, Settings, Shield, Plus, Database, Repeat, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import SiteBanner from '../../components/SiteBanner';
import { canAccess, isViewOnly, getAdminRole, getRoleDisplayName, getRoleBadgeColor } from '../../utils/adminRole';
import { AdminStorage } from '../../utils/storage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [adminRole, setAdminRole] = useState('staff');

  useEffect(() => {
    // Admin auth now lives in an httpOnly cookie; this checks the cached
    // profile written at login (sessionStorage, auto-clears on tab close).
    const adminData = AdminStorage.getAdmin();

    if (!adminData) {
      navigate('/admin/login');
      return;
    }

    setAdmin(adminData);
    setAdminRole(getAdminRole());
  }, [navigate]);

  const handleLogout = async () => {
    try {
      // Revoke refresh token + clear the httpOnly cookies server-side
      // (JS can no longer delete them directly).
      await axios.post(`${API}/admin/logout`, {});
    } catch (_) {
      // Best-effort — clear local cache regardless.
    }
    AdminStorage.clear();
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const allMenuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, resource: 'dashboard' },
    { path: '/admin/pending', label: 'Pending Requests', icon: ClipboardList, resource: 'pending_requests' },
    { path: '/admin/bookings', label: 'Bookings', icon: Calendar, resource: 'bookings' },
    { path: '/admin/bookings/series', label: 'Recurring Series', icon: Repeat, resource: 'bookings' },
    { path: '/admin/manual-booking', label: 'Manual Booking', icon: Plus, resource: 'manual_booking' },
    { path: '/admin/waitlist', label: 'Waitlist', icon: Clock, resource: 'waitlist' },
    { path: '/admin/release-booking', label: 'Release Booking', icon: Unlock, resource: 'release_paid_booking' },
    { path: '/admin/customers', label: 'Customers', icon: Users, resource: 'customers' },
    { path: '/admin/staff', label: 'Staff', icon: UserCog, resource: 'staff' },
    { path: '/admin/pricing', label: 'Pricing', icon: DollarSign, resource: 'pricing' },
    { path: '/admin/settings/holidays', label: 'Holidays', icon: Calendar, resource: 'holidays' },
    { path: '/admin/locations', label: 'Locations', icon: MapPin, resource: 'locations' },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3, resource: 'analytics' },
    { path: '/admin/settings', label: 'Settings', icon: Settings, resource: 'settings' },
    { path: '/admin/integrations', label: 'Integrations', icon: Settings, resource: 'integrations' },
    { path: '/admin/security', label: 'Security', icon: Shield, resource: 'security' },
    { path: '/admin/db-tools', label: 'DB Tools', icon: Database, resource: 'db_tools' }
  ];

  // Filter menu items based on role
  const menuItems = allMenuItems.filter(item => canAccess(item.resource));

  const isActive = (path) => location.pathname === path;

  return (
    <div data-admin-scope="true" className="admin-container flex flex-col min-h-screen">
      <SiteBanner scope="admin" />
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <div className="admin-sidebar w-64 p-6 flex flex-col flex-shrink-0 bg-gradient-to-b from-emerald-700 to-emerald-600">
        <div className="mb-8">
          <h1 className="text-2xl font-black font-heading uppercase text-white">SmashZone</h1>
          <p className="text-sm text-emerald-100 mt-1">Admin Panel</p>
          {admin && (
            <>
              <p className="text-xs text-emerald-200/80 mt-2">{admin.email}</p>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold mt-2 ${getRoleBadgeColor(adminRole)}`}>
                {getRoleDisplayName(adminRole)}
              </span>
            </>
          )}
        </div>

        <nav className="flex-1 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const viewOnly = isViewOnly(item.resource);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`menu-${item.path.split('/').pop()}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-white ${
                  isActive(item.path) ? 'bg-emerald-800 shadow-lg' : 'hover:bg-emerald-600/50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-bold uppercase text-sm">{item.label}</span>
                {viewOnly && (
                  <span className="ml-auto text-[10px] bg-white/20 px-2 py-0.5 rounded-full">
                    VIEW
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="admin-logout-button"
          className="w-full justify-start text-white hover:bg-white/20"
        >
          <LogOut className="w-5 h-5 mr-3" />
          <span className="font-bold uppercase text-sm">Logout</span>
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <Outlet />
      </div>
      </div>
    </div>
  );
}